import { SystemArtifactState } from '@lib/signalr/signalr.types';
import { z } from 'zod';

export const systemSearchSchema = z.object({
  page: z.number().min(1).default(1).catch(1),
  pageSize: z.number().min(1).max(100).default(20).catch(20),
  isOnline: z.boolean().optional().catch(undefined),
  searchTerm: z.string().optional().catch(undefined),
});

export type ListSystemsQuery = z.infer<typeof systemSearchSchema>;

export interface UploadSystemArtifactsRequest {
  systemId: string;
  files: File[];
}

export interface GetSystemArtifactsQuery {
  systemId: string;
}

export interface DownloadSystemArtifactsQuery {
  artifactId: string;
}

export interface RequestSystemLogsRequest {
  systemId: string;
}

export enum InstallationEventType {
  Installed,
  Uninstalled,
}

export enum CadLinkRole {
  MasterServer = 0,
  RegionalServer = 1,
  StandaloneServer = 2,
  Client = 3,
}

export enum CadLinkConnectionType {
  ClientToServer = 0,
  RegionalToMaster = 1,
}

// --- Zod schemas for API response validation ---

export const systemEnvironmentDtoSchema = z.object({
  capturedAt: z.string(),
  machineName: z.string(),
  machineManufacturer: z.string(),
  machineModel: z.string(),
  cpuName: z.string(),
  cpuPhysicalProcessors: z.number(),
  cpuLogicalProcessors: z.number(),
  cpuClockSpeed: z.number(),
  memoryTotalPhysicalMb: z.number(),
  operatingSystemName: z.string(),
  operatingSystemVersion: z.string(),
  operatingSystemSerialNumber: z.string(),
  domain: z.string(),
  currentTimeZone: z.number(),
  daylightSavingsInEffect: z.boolean(),
  locale: z.string(),
  agentDotNetVersion: z.string(),
  countryCode: z.string().nullable(),

  cadLinkClientCadLinkId: z.string().nullable(),
  cadLinkClientIsThin: z.boolean(),
  cadLinkClientUsingServer: z.boolean(),
  cadLinkClientServerUrl: z.string().nullable(),
  cadLinkClientServerPort: z.number().nullable(),
  cadLinkClientServerId: z.string().nullable(),
  cadLinkClientServerDatabaseId: z.string().nullable(),

  cadLinkServerCadLinkId: z.string().nullable(),
  cadLinkServerDatabaseId: z.string().nullable(),
  cadLinkServerUrl: z.string().nullable(),
  cadLinkServerPort: z.number().nullable(),
  cadLinkServerPublicName: z.string().nullable(),
  cadLinkServerIsMaster: z.boolean(),
  cadLinkServerMasterUrl: z.string().nullable(),
  cadLinkServerMasterCadLinkId: z.string().nullable(),
});

export const listSystemDtoSchema = z.object({
  id: z.string(),
  registeredAt: z.string(),
  isOnline: z.boolean(),
  isOnlineChangedAt: z.string(),
  lastCheckedAt: z.string().nullable(),
  machineName: z.string().nullable(),
});

export const listSystemsResponseSchema = z.object({
  systems: z.array(listSystemDtoSchema),
  totalCount: z.number(),
  page: z.number(),
  pageSize: z.number(),
});

export const getSystemResponseSchema = z.object({
  id: z.string(),
  registeredAt: z.string(),
  isOnline: z.boolean(),
  isOnlineChangedAt: z.string(),
  lastCheckedAt: z.string().nullable(),
  currentEnvironment: systemEnvironmentDtoSchema.nullable(),
});

const installationEventTypeSchema = z.nativeEnum(InstallationEventType);

const cadwellApplicationHistoryDtoSchema = z.object({
  id: z.string(),
  applicationName: z.string(),
  version: z.string(),
  eventType: installationEventTypeSchema,
  capturedAt: z.string(),
  isCurrentlyInstalled: z.boolean(),
  installLocation: z.string().nullable(),
  installDate: z.string().nullable(),
});

export const getCadwellApplicationHistoryResponseSchema = z.object({
  systemId: z.string(),
  applications: z.array(cadwellApplicationHistoryDtoSchema),
});

export const uploadSystemArtifactsResponseSchema = z.object({
  message: z.string(),
});

const systemArtifactStateSchema = z.nativeEnum(SystemArtifactState);

const systemArtifactSchema = z.object({
  id: z.string(),
  blobName: z.string(),
  readableName: z.string(),
  uploadedAt: z.string(),
  uploadedBy: z.string(),
  fileSizeBytes: z.number(),
  contentType: z.string(),
  state: systemArtifactStateSchema,
  requestedAt: z.string().nullable(),
  autoDeleteAt: z.string(),
});

export const getSystemArtifactsResponseSchema = z.object({
  artifacts: z.array(systemArtifactSchema),
});

export const downloadSystemArtifactsResponseSchema = z.object({
  downloadUrl: z.string(),
});

export const requestSystemLogsResponseSchema = z.object({
  artifactId: z.string(),
  message: z.string(),
});

const cadLinkRoleSchema = z.nativeEnum(CadLinkRole);
const cadLinkConnectionTypeSchema = z.nativeEnum(CadLinkConnectionType);

const installedApplicationSchema = z.object({
  name: z.string(),
  version: z.string(),
});

const topologyNodeSchema = z.object({
  systemId: z.string(),
  hostname: z.string().nullable(),
  role: cadLinkRoleSchema,
  isOnline: z.boolean(),
  isOnlineChangedAt: z.string().nullable(),
  dataLastRefreshedAt: z.string().nullable(),
  cadLinkClientVersion: z.string().nullable(),
  cadLinkServerVersion: z.string().nullable(),
  cadLinkId: z.string().nullable(),
  serverDatabaseId: z.string().nullable(),
  isThinClient: z.boolean().nullable(),
  isPhantom: z.boolean(),
  products: z.array(z.string()),
  customerId: z.string().nullable(),
  customerName: z.string().nullable(),
  installedApplications: z.array(installedApplicationSchema),
  connectedChildCount: z.number(),
});

const topologyEdgeSchema = z.object({
  sourceSystemId: z.string(),
  targetSystemId: z.string(),
  connectionType: cadLinkConnectionTypeSchema,
});

export const cadLinkTopologyResponseSchema = z.object({
  isCadLinkEnvironment: z.boolean(),
  nodes: z.array(topologyNodeSchema),
  edges: z.array(topologyEdgeSchema),
});

export type SystemEnvironmentDto = z.infer<typeof systemEnvironmentDtoSchema>;
export type ListSystemDto = z.infer<typeof listSystemDtoSchema>;
export type ListSystemsResponse = z.infer<typeof listSystemsResponseSchema>;
export type GetSystemResponse = z.infer<typeof getSystemResponseSchema>;
export type CadwellApplicationHistoryDto = z.infer<typeof cadwellApplicationHistoryDtoSchema>;
export type GetCadwellApplicationHistoryResponse = z.infer<
  typeof getCadwellApplicationHistoryResponseSchema
>;
export type UploadSystemArtifactsResponse = z.infer<typeof uploadSystemArtifactsResponseSchema>;
export type SystemArtifact = z.infer<typeof systemArtifactSchema>;
export type GetSystemArtifactsResponse = z.infer<typeof getSystemArtifactsResponseSchema>;
export type DownloadSystemArtifactsResponse = z.infer<typeof downloadSystemArtifactsResponseSchema>;
export type RequestSystemLogsResponse = z.infer<typeof requestSystemLogsResponseSchema>;
export type InstalledApplication = z.infer<typeof installedApplicationSchema>;
export type TopologyNode = z.infer<typeof topologyNodeSchema>;
export type TopologyEdge = z.infer<typeof topologyEdgeSchema>;
export type CadLinkTopologyResponse = z.infer<typeof cadLinkTopologyResponseSchema>;

export const cadLinkServerClientsResponseSchema = z.object({
  nodes: z.array(topologyNodeSchema),
  totalCount: z.number(),
});
export type CadLinkServerClientsResponse = z.infer<typeof cadLinkServerClientsResponseSchema>;
